import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import {
  ApiNewDokumentPowiazanyDalDto,
  AttachmentsComponent,
  ColumnHeader,
  DocSignService,
  EgibObject,
  FormAlertService,
  LawPersonSimpleFormComponent,
  MapAction,
  MapExtentUtils,
  MapId,
  MapObject,
  MapObjectApiType,
  MapObjectTableActionType,
  MapObjectTableState,
  MapPortalName,
  MapSettings,
  MapSettingsService,
  MapState,
  MapStateService,
  MapViewActionType,
  PortalId,
  PzService,
  ShapeUtils,
  StreetService,
  ToolType,
} from '@gk/gk-modules';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import {
  catchError,
  forkJoin,
  from,
  map,
  Observable,
  of,
  switchMap,
  takeWhile,
  tap,
  throwError,
} from 'rxjs';
import { MainRoutes } from '../../../guards/guard.models';
import { NewRequestHelperService } from '../../../services/new-request-helper/new-request-helper.service';
import { PlaceService } from '../../../services/place/place.service';
import { BsMessageType } from '../../../services/request-workspace-state/request-workspace-state.model';
import { BaseNewRequestComponent } from '../../../shared/base-new-request/base-new-request.component';
import { RequestReplyFormComponent } from '../../../shared/request-reply-form/request-reply-form.component';
import { DocsTableEditableComponent } from '../../designer-portal/new-designer-request/docs-table-editable/docs-table-editable.component';
import {
  WorkspaceStateDraftVersion,
  WorkspaceStateId,
} from './../../../services/request-workspace-state/request-workspace-state.model';
import {
  FellingObjectsControlName,
  FellingPurpose,
  TASFNewRequestControlName,
} from './services/new-request-form/new-request-form.model';
import { TASFNewRequestFormService } from './services/new-request-form/new-request-form.service';
import {
  TASFNewRequest,
  TASFNewRequestDto,
  TreesAndShrubsFellingRequestFormWorkspaceState,
} from './services/new-request/new-request.model';

@Component({
  selector: 'app-trees-and-shrubs-felling',
  templateUrl: './trees-and-shrubs-felling-request.component.html',
  standalone: false,
})
export class TreesAndShrubsFellingRequestComponent
  extends BaseNewRequestComponent
  implements OnInit, OnDestroy
{
  @ViewChild(LawPersonSimpleFormComponent)
  lawPersonSimpleFormComponent: LawPersonSimpleFormComponent;
  @ViewChild(RequestReplyFormComponent)
  requestReplyFormComponent: RequestReplyFormComponent;
  @ViewChild(DocsTableEditableComponent)
  docsTableEditableComponent: DocsTableEditableComponent;
  @ViewChild(AttachmentsComponent) attachmentsComponent: AttachmentsComponent;
  override controlName = TASFNewRequestControlName;
  mapObjectTableState = new MapObjectTableState(
    [
      new ColumnHeader('mapObjectNumber', 'PARCEL_SEARCH_FORM.PARCEL_NUMBER'),
      new ColumnHeader('districtName', 'PARCEL_SEARCH_FORM.DISTRICT'),
      new ColumnHeader('mapSheet', 'PARCEL_SEARCH_FORM.SHEET'),
      new ColumnHeader('typeName', 'GK.MAP.TYPE', true),
      new ColumnHeader('area', 'GK.MAP.AREA_IN_HA'),
    ],
    undefined,
    this.defaultMapGeometryStyles,
    true,
    true,
    true,
    '25',
  );
  formValue: TASFNewRequest;
  formNotValidTranslation: string;
  fellingObjectsControlName = FellingObjectsControlName;
  fellingPurpose = FellingPurpose;
  override portalId =
    PortalId.EnvironmentalProtectionPortalTreesAndShrubsFellingRequest;
  workspaceStateDraftVersion =
    WorkspaceStateDraftVersion.TreesAndShrubsFellingRequestFormVersion;
  workspaceStateId = WorkspaceStateId.TreesAndShrubsFellingRequestForm;

  constructor(
    private tasfNewRequestFormService: TASFNewRequestFormService,
    public override mapStateService: MapStateService,
    public override mapSettingsService: MapSettingsService,
    private toastr: ToastrService,
    public override translateService: TranslateService,
    public override docSignService: DocSignService,
    public override newRequestHelperService: NewRequestHelperService,
    public override router: Router,
    public override formAlertService: FormAlertService,
    public override placeService: PlaceService,
    public override streetService: StreetService,
    public override pzService: PzService,
  ) {
    super(
      pzService,
      newRequestHelperService,
      docSignService,
      router,
      translateService,
      mapSettingsService,
      mapStateService,
      formAlertService,
      placeService,
      streetService,
    );
  }

  override ngOnInit(): void {
    this.createForm();
    super.subscribeToDocSignTranslations();
    this.subscribeToFormNotValidTranslation();
    this.fetchApplicantMapSettings();
    this.fetchWrongFileText();
    this.updateInvestmentDescriptionRequirementOnBusinessPurposeChange();
  }

  createForm(): void {
    this.formGroup = this.tasfNewRequestFormService.getFormGroup();
    this.disableOrEnableStreetFormControlOnStreetAbsenceCheckboxChange();
  }

  subscribeToFormNotValidTranslation(): void {
    this.translateService
      .get(
        'ENVIRONMENTAL_PROTECTION_PORTAL.TREES_AND_SHRUBS_FELLING.VALIDATION.FORM_NOT_VALID_ERROR',
      )
      .pipe(takeWhile(() => this.isAlive))
      .subscribe(
        (formNotValidTranslation) =>
          (this.formNotValidTranslation = formNotValidTranslation),
      );
  }

  override setMapState(mapSettings: MapSettings): void {
    this.mapState = new MapState(
      MapId.NewEnvironmentalProtectionPortalRequest,
      this.mapStateService.getViewState(
        MapId.NewEnvironmentalProtectionPortalRequest,
        mapSettings,
      ),
      this.mapStateService.getToolbarState(
        [ToolType.LandParcel, ToolType.AnyPolygon],
        mapSettings.papers,
        undefined,
        undefined,
        true,
      ),
      this.initialToolsState,
      this.mapStateService.getLayersState(
        MapId.NewEnvironmentalProtectionPortalRequest,
        mapSettings,
        MapPortalName.Applicant,
      ),
      [this.mapObjectTableState],
    );
  }

  areParcelsChosen(): boolean {
    return this.mapState.mapObjectTablesState[0].mapObjects.some(
      (mapObject) => mapObject.type === MapObjectApiType.LandParcel,
    );
  }

  handleSubmit(): void {
    this.submitted = true;
    if (!this.isRequestValid()) {
      this.toastr.error(this.formNotValidTranslation);
      return;
    }

    this.sendRequest();
  }

  sendRequest(): void {
    forkJoin([
      this.lawPersonSimpleFormComponent.askForPostOfficeWhenPostalCodeIsNotFromDictionary(),
      this.requestReplyFormComponent.askForPostOfficeWhenPostalCodeIsNotFromDictionary(),
    ])
      .pipe(
        tap(() => {
          this.docSignPending = true;
          this.setDocSignMsg(BsMessageType.Info, 'SENDING_REQUEST');
        }),
        switchMap(() => this.getRequestBody()),
        switchMap((body) =>
          this.docSignService.addToSign(
            body,
            '/api/interesant/wniosek/wycinkaDrzewIKrzewow/addToSign',
          ),
        ),
        takeWhile(() => this.isAlive),
      )
      .subscribe({
        next: (addedDocToSignResponse) => {
          this.handleSendAndValidateSuccess(addedDocToSignResponse);
        },
        error: () => this.handleSendAndValidateFailure(),
      });
  }

  getRequestBody = (): Observable<TASFNewRequestDto> => {
    return from(
      Promise.all(this.attachmentsComponent.getConvertedFiles()).catch(
        (error) => new Error(error),
      ),
    ).pipe(
      switchMap((apiFiles) => {
        const apiFilesArray = Object.values(apiFiles).flatMap((file) => file);
        if (
          (Array.isArray(apiFilesArray) &&
            apiFilesArray.some((file) => file instanceof Error)) ||
          apiFiles instanceof Error
        ) {
          this.toastr.error(this.wrongFilesErrorText);

          return throwError(() => new Error(this.wrongFilesErrorText));
        }

        return of(apiFiles);
      }),
      map((apiFiles) =>
        TASFNewRequest.fromAppToApi(
          this.getFormValue(),
          this.getParcelMapObject(),
          this.getIssueRangesMapObjects(),
          apiFiles as ApiNewDokumentPowiazanyDalDto[],
        ),
      ),
      catchError((err) => {
        console.error(err);

        return throwError(() => err);
      }),
    );
  };

  getWorkspaceStateToSave =
    (): TreesAndShrubsFellingRequestFormWorkspaceState => ({
      formValue: this.getFormValue(),
      parcels: this.getParcelMapObject(),
      issueRanges: this.getIssueRangesMapObjects(),
    });

  handleLoadWorkspaceResponse(
    state: TreesAndShrubsFellingRequestFormWorkspaceState,
  ): void {
    if (state.formValue.shrubsList) {
      state.formValue.shrubsList.forEach(() => this.addShrubFormControl());
    }
    if (state.formValue.treesList) {
      state.formValue.treesList.forEach(() => this.addTreeFormControl());
    }
    const { documentationForm, ...formValue } = state.formValue;
    this.formGroup.patchValue(formValue);
    this.requestReplyFormComponent.patchRequestReplyFormControls({
      documentationForm: documentationForm,
      shippingForm: state.formValue.shippingForm,
    });
    this.handleMapAction(
      new MapAction(MapObjectTableActionType.AddNew, [...state.parcels]),
    );
    if (state.issueRanges) {
      const mapObjects = state.issueRanges.map((workRange) =>
        ShapeUtils.getAnyGeometryMapObject(
          workRange,
          MapObjectApiType.ExtentOrPolygon,
        ),
      );
      this.handleMapAction(
        new MapAction(MapObjectTableActionType.AddToExisting, mapObjects),
      );
      this.handleMapAction(
        new MapAction(
          MapViewActionType.ExtentToFitToChange,
          MapExtentUtils.getMapExtentFromMapObjects(mapObjects),
        ),
      );
      this.handleMapAction(
        new MapAction(MapObjectTableActionType.SelectRange, mapObjects),
      );
    }
  }

  override handleDocSignSuccess(): void {
    this.docSignUrl = '';
    this.router.navigateByUrl(
      `/${MainRoutes.EnvironmentalProtectionPortalTreesAndShrubsFellingRequest}/requests-list`,
    );
  }

  isRequestValid(): boolean {
    return (
      this.formGroup.valid &&
      this.areParcelsChosen() &&
      this.areIssueRangesChosen() &&
      this.attachmentsComponent.areDocumentsValid()
    );
  }

  areIssueRangesChosen(): boolean {
    return this.mapState.mapObjectTablesState[0].mapObjects.some(
      (mapObject) => mapObject.type === MapObjectApiType.ExtentOrPolygon,
    );
  }

  getFormValue(): TASFNewRequest {
    return this.formGroup.getRawValue();
  }

  getParcelMapObject(): EgibObject[] {
    return (this.mapState.mapObjectTablesState[0].mapObjects || []).filter(
      <(mapObject: MapObject) => mapObject is EgibObject>(
        ((mapObject) => mapObject.type === MapObjectApiType.LandParcel)
      ),
    );
  }

  getIssueRangesMapObjects(): string[] {
    return (this.mapState.mapObjectTablesState[0].mapObjects || [])
      .filter(
        (mapObject) => mapObject.type === MapObjectApiType.ExtentOrPolygon,
      )
      .map((mapObject) => mapObject.geom);
  }

  checkParcelsAndIssueRangeValidAndFormValidated(): boolean {
    return (
      (!this.areParcelsChosen() || !this.areIssueRangesChosen()) &&
      this.submitted
    );
  }

  checkTreeFormGroupValidAndFormValidated(index: number): boolean {
    return !this.getTreeFormGroup(index).valid && this.submitted;
  }

  getTreeInventoryNumberFormControl(index: number): UntypedFormControl {
    return this.getTreeFormGroup(index).get(
      FellingObjectsControlName.InventoryNumber,
    ) as UntypedFormControl;
  }

  getTreeSpeciesNameFormControl(index: number): UntypedFormControl {
    return this.getTreeFormGroup(index).get(
      FellingObjectsControlName.SpeciesName,
    ) as UntypedFormControl;
  }

  getTreeTrunkCircumferenceFormControl(index: number): UntypedFormControl {
    return this.getTreeFormGroup(index).get(
      FellingObjectsControlName.TrunkCircumference,
    ) as UntypedFormControl;
  }

  getTreeFormGroup(index: number): UntypedFormGroup {
    return this.getTreesListFormArray().controls[index] as UntypedFormGroup;
  }

  deleteTreeFormControl(index: number): void {
    this.getTreesListFormArray().removeAt(index);
  }

  areSomeTreesOrShrubsFormGroups(): boolean {
    return this.areSomeTreesFormGroups() || this.areSomeShrubsFormGroups();
  }

  areSomeTreesFormGroups(): boolean {
    return !!this.getTreesListFormArray().controls.length;
  }

  areSomeShrubsFormGroups(): boolean {
    return !!this.getShrubsListFormArray().controls.length;
  }

  addTreeFormControl(): void {
    this.getTreesListFormArray().push(
      this.tasfNewRequestFormService.getTreeFormGroup(),
    );
  }

  getTreesListFormArray(): UntypedFormArray {
    return this.formGroup.get(
      TASFNewRequestControlName.TreesList,
    ) as UntypedFormArray;
  }

  checkShrubFormGroupValidAndFormValidated(index: number): boolean {
    return !this.getShrubFormGroup(index).valid && this.submitted;
  }

  getShrubInventoryNumberFormControl(index: number): UntypedFormControl {
    return this.getShrubFormGroup(index).get(
      FellingObjectsControlName.InventoryNumber,
    ) as UntypedFormControl;
  }

  getShrubSpeciesNameFormControl(index: number): UntypedFormControl {
    return this.getShrubFormGroup(index).get(
      FellingObjectsControlName.SpeciesName,
    ) as UntypedFormControl;
  }

  getShrubProjectionSurfaceFormControl(index: number): UntypedFormControl {
    return this.getShrubFormGroup(index).get(
      FellingObjectsControlName.ProjectionSurface,
    ) as UntypedFormControl;
  }

  getShrubFormGroup(index: number): UntypedFormGroup {
    return this.getShrubsListFormArray().controls[index] as UntypedFormGroup;
  }

  deleteShrubFormControl(index: number): void {
    this.getShrubsListFormArray().removeAt(index);
  }

  addShrubFormControl(): void {
    this.getShrubsListFormArray().push(
      this.tasfNewRequestFormService.getShrubFormGroup(),
    );
  }

  getShrubsListFormArray(): UntypedFormArray {
    return this.formGroup.get(
      TASFNewRequestControlName.ShrubsList,
    ) as UntypedFormArray;
  }

  checkFellingCauseFormControlValidAndFormValidated(): boolean {
    return !this.getFellingCauseFormControl().valid && this.submitted;
  }

  getFellingCauseFormControl(): UntypedFormControl {
    return this.formGroup.get(
      TASFNewRequestControlName.FellingCause,
    ) as UntypedFormControl;
  }

  checkFellingDateFormControlValidAndFormValidated(): boolean {
    return !this.getFellingDateFormControl().valid && this.submitted;
  }

  getFellingDateFormControl(): UntypedFormControl {
    return this.formGroup.get(
      TASFNewRequestControlName.FellingDate,
    ) as UntypedFormControl;
  }

  checkInvestmentDescriptionFormControlValidAndFormValidated(): boolean {
    return !this.getInvestmentDescriptionFormControl().valid && this.submitted;
  }

  getInvestmentDescriptionFormControl(): UntypedFormControl {
    return this.formGroup.get(
      TASFNewRequestControlName.InvestmentDescription,
    ) as UntypedFormControl;
  }

  checkGrowthPlaceFormControlValidAndFormValidated(): boolean {
    return !this.getGrowthPlaceFormControl().valid && this.submitted;
  }

  getGrowthPlaceFormControl(): UntypedFormControl {
    return this.formGroup.get(
      TASFNewRequestControlName.GrowthPlace,
    ) as UntypedFormControl;
  }

  checkBusinessPurposeFormControlValidAndFormValidated(): boolean {
    return !this.getBusinessPurposeFormControl().valid && this.submitted;
  }

  updateInvestmentDescriptionRequirementOnBusinessPurposeChange(): void {
    const investmentDescriptionFormControl =
      this.getInvestmentDescriptionFormControl();
    this.getBusinessPurposeFormControl()
      .valueChanges.pipe(takeWhile(() => this.isAlive))
      .subscribe((value) => {
        if (value === FellingPurpose.Business) {
          investmentDescriptionFormControl.setValidators([Validators.required]);
        } else {
          investmentDescriptionFormControl.clearValidators();
        }
        investmentDescriptionFormControl.updateValueAndValidity();
      });
  }

  getBusinessPurposeFormControl(): UntypedFormControl {
    return this.formGroup.get(
      TASFNewRequestControlName.BusinessPurpose,
    ) as UntypedFormControl;
  }

  isLocationFormGroupValid(): boolean {
    return (
      this.isLandRegisterFormControlValid() &&
      this.isPlaceFormControlValid() &&
      this.isStreetFormControlValid()
    );
  }

  isStreetFormControlValid(): boolean {
    const streetFormControl = this.getStreetFormControl();
    return streetFormControl.disabled || streetFormControl.valid;
  }

  isPlaceFormControlValid(): boolean {
    return this.getPlaceFormControl().valid;
  }

  override getPlaceFormControl(): UntypedFormControl {
    return this.formGroup.get(
      TASFNewRequestControlName.Place,
    ) as UntypedFormControl;
  }

  isLandRegisterFormControlValid(): boolean {
    return this.getLandRegisterFormControl().valid;
  }

  getLandRegisterFormControl(): UntypedFormControl {
    return this.formGroup.get(
      TASFNewRequestControlName.LandRegister,
    ) as UntypedFormControl;
  }

  getLandNumberFormControl(): UntypedFormControl {
    return this.formGroup.get(
      TASFNewRequestControlName.LandNumber,
    ) as UntypedFormControl;
  }

  disableOrEnableStreetFormControlOnStreetAbsenceCheckboxChange(): void {
    const streetAbsenceFormControl = this.getStreetAbsenceFormControl();
    if (!streetAbsenceFormControl) {
      return;
    }
    const streetFormControl = this.getStreetFormControl();
    streetAbsenceFormControl.valueChanges
      .pipe(takeWhile(() => this.isAlive))
      .subscribe((value) => {
        if (value) {
          streetFormControl.disable();
        } else if (this.getStreetAbsenceFormControl().enabled) {
          streetFormControl.enable();
        }
      });
  }

  getStreetAbsenceFormControl(): UntypedFormControl {
    return this.formGroup.get(
      TASFNewRequestControlName.StreetAbsence,
    ) as UntypedFormControl;
  }

  ngOnDestroy(): void {
    this.isAlive = false;
  }
}
